import { differenceInCalendarDays } from 'date-fns'
import {
  type BannerStatus,
  type UserDocumentRenewalSectionData,
  type BannerEntry,
  type RenewalDocumentInput,
} from './types'

const toBannerEntry = (
  document: RenewalDocumentInput,
  now: Date
): BannerEntry | null => {
  const { expirationDate, notificationTime } = document

  if (!expirationDate || !notificationTime) return null

  const expiration = new Date(expirationDate)
  const notification = new Date(notificationTime)

  if (
    Number.isNaN(expiration.getTime()) ||
    Number.isNaN(notification.getTime())
  ) {
    return null
  }

  if (notification > now) return null

  const status: BannerStatus =
    differenceInCalendarDays(expiration, now) < 0 ? 'overdue' : 'expiringSoon'

  return {
    item: {
      id: document.id,
      documentName: document.name,
      userId: document.user.id,
      userName: document.user.fullName,
      expirationDate: expiration,
    },
    status,
  }
}

export const buildUserDocumentRenewalSections = (params: {
  documents: RenewalDocumentInput[]
  now?: Date
}): UserDocumentRenewalSectionData[] => {
  const { documents, now = new Date() } = params
  const entriesById = new Map<string, BannerEntry>()

  for (const document of documents) {
    const entry = toBannerEntry(document, now)
    if (entry) entriesById.set(entry.item.id, entry)
  }

  const entries = [...entriesById.values()].sort(
    (a, b) => a.item.expirationDate.getTime() - b.item.expirationDate.getTime()
  )

  const sections: UserDocumentRenewalSectionData[] = [
    {
      id: 'overdue',
      status: 'overdue',
      items: entries
        .filter(entry => entry.status === 'overdue')
        .map(entry => entry.item),
    },
    {
      id: 'expiringSoon',
      status: 'expiringSoon',
      items: entries
        .filter(entry => entry.status === 'expiringSoon')
        .map(entry => entry.item),
    },
  ]

  return sections.filter(section => section.items.length > 0)
}

export const formatUserDocumentRenewalStatus = (params: {
  expirationDate: Date
  now?: Date
}) => {
  const { expirationDate, now = new Date() } = params
  const dayDiff = differenceInCalendarDays(expirationDate, now)

  return dayDiff < 0
    ? {
        key: 'header.user-document-renewal.expired-days' as const,
        value: -dayDiff,
      }
    : {
        key: 'header.user-document-renewal.expires-in-days' as const,
        value: dayDiff,
      }
}
